import ReactECharts, { EChartsOption } from 'echarts-for-react';
import { isNumber } from 'lodash-es';
import { type JSX } from 'react';

import { CSSColors } from 'Components/Colors';

import { useStableChartOption } from './StableChartOption';
import { ChartAxis, ChartSeries, axisColor, chartColor, chartFont } from './charts.common';

export interface LineChartProps {
    xAxisLabels: string[];
    /**
     * If provided, these labels will be used within the hover/tooltip, rather than the labels in `xAxisLabels`.
     * For instance, if you want to show short dates on the x-axis and long dates in the tooltip, set this prop to the long dates.
     */
    xAxisLabelsForTooltips?: string[];
    /** `position` must be 'left'. Must have a `name`. */
    yAxisInfo: ChartAxis;
    /**
     * One or more sets of data points, each of which correponds with a single line.
     * If multiple lines are provided, each should have a unique `color`. Each element's `data` array should have a length equal to the length of `xAxisLabels` (as well as `xAxisLabelsForTooltips`, if provided).
     */
    lines: ChartSeries[];
    /** Used for label colors (this chart type is used outside of dark-themed Dashboards). 'dark' is the default. */
    variant?: 'dark' | 'light';
    paddingVariant?: 'sm' | 'lg';
}

/**
 * A line chart. Supports multiple lines.
 */
export const LineChart = (props: LineChartProps): JSX.Element => {
    const primaryFont = getComputedStyle(document.documentElement).getPropertyValue(chartFont);
    const labelColor = props.variant === 'light' ? CSSColors.BLUE : getComputedStyle(document.documentElement).getPropertyValue(chartColor);

    const ariaDescription = (() => {
        const getValueDescriptionForLine = (xAxisIndex: number, line: ChartSeries) => {
            const dataPoint = line.data[xAxisIndex];
            const valueAsNumber = isNumber(dataPoint) ? dataPoint : dataPoint.value;
            return `${valueAsNumber} (${line.name})`;
        };

        const getLineDescriptions = (xAxisIndex: number): string => {
            return props.lines.map((line) => getValueDescriptionForLine(xAxisIndex, line)).join(', ');
        };

        const xAxisLabels = props.xAxisLabelsForTooltips ?? props.xAxisLabels;
        const valuesDescriptions = xAxisLabels.map((xAxisLabel, xAxisIndex) => `For ${xAxisLabel}, the values of the lines are ${getLineDescriptions(xAxisIndex)}.`).join(' ');
        return `A line chart showing ${props.yAxisInfo.name!} with ${props.lines.length} lines. ${valuesDescriptions}`;
    })();

    const getOption = (info: LineChartProps, primaryFont: string, labelColor: string): EChartsOption => {
        // Add a non functional axis to complete the grid
        const yAxes: ChartAxis[] = [info.yAxisInfo, { name: '', position: 'right' }];

        return {
            tooltip: {
                trigger: 'item',
            },
            legend: { show: props.lines.length > 1, type: 'scroll', bottom: 0, textStyle: { color: labelColor } },
            textStyle: {
                fontFamily: primaryFont,
            },
            grid: {
                containLabel: true,
                left: props.paddingVariant === 'sm' ? '3%' : '10%',
                right: props.paddingVariant === 'sm' ? '3%' : '10%',
                top: '10%',
                // The legend only shows if there are multiple lines. If the legend shows, increase spacing between the x-axis and the bottom of the chart, so the legend isn't too close to the x-axis.
                bottom: props.lines.length > 1 ? '18%' : '10%',
            },
            xAxis: [
                {
                    type: 'category',
                    boundaryGap: false,
                    axisTick: {
                        alignWithLabel: true,
                    },
                    data: info.xAxisLabelsForTooltips ?? info.xAxisLabels.map((label) => label.toUpperCase()),
                    axisLine: {
                        show: true,
                        lineStyle: {
                            color: axisColor,
                        },
                    },
                    axisLabel: {
                        color: labelColor,
                        formatter: info.xAxisLabelsForTooltips ? (_value: string, index: number) => info.xAxisLabels[index] : undefined,
                    },
                },
            ],
            yAxis: yAxes.map((yAxis) => ({
                type: 'value',
                name: yAxis.name?.toUpperCase(),
                position: yAxis.position,
                min: yAxis.min,
                max: yAxis.max,
                minInterval: yAxis.minInterval,
                maxInterval: yAxis.maxInterval,
                interval: yAxis.interval,
                nameRotate: yAxis.position === 'left' ? 90 : 270,
                alignTicks: true,
                show: true,
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: axisColor,
                    },
                },
                axisLabel: {
                    formatter: yAxis.labelFormatter,
                    color: labelColor,
                },
                nameLocation: 'middle',
                nameGap: 50,
                splitLine: {
                    lineStyle: {
                        color: axisColor,
                    },
                },
                nameTextStyle: {
                    color: labelColor,
                },
            })),
            series: info.lines.map((line) => ({
                name: line.name,
                type: 'line',
                emphasis: {
                    focus: 'series',
                },
                itemStyle: {
                    color: line.color,
                },
                data: line.data,
                symbol: 'circle',
                symbolSize: 10,
            })),
        };
    };

    // The x-axis label formatter reads `xAxisLabels`, which is not otherwise in the option when `xAxisLabelsForTooltips` is provided.
    const option = useStableChartOption(getOption(props, primaryFont, labelColor), [props.xAxisLabels]);

    return (
        <ReactECharts
            aria-label={ariaDescription}
            role="img"
            option={option}
            style={{
                height: '100%',
                width: '100%',
                minHeight: '300px',
                minWidth: '400px',
                maxHeight: '300px',
            }}
            // Buckle up for the history leading up to the use of this `notMerge` prop, which may or may not be the best thing to do:
            // 1. Charts were flickering. Details on why and when, which may or may not be accurate, can be viewed in PR #2109.
            // 2. That PR introduced `useRef` to each chart component. I assume that solved the problem, since the PR was merged and flickering was not mentioned/addressed in following PRs. But I don't understand how the fix works:
            //    - With `props` as a dependency of the `useEffect`, rather than `props.onClick`, the effect will be called any time any props change. So I don't think the ECharts instance was having its options updated any less frequently.
            //    - On top of that, the effect is adding on-click callbacks to a *list* of on-click handlers, which means every time the effect was running, a new on-click handler was being added. This happened not to cause any problems (that we were aware of) - in the case of this component, mainly because `onChartClick` was never defined as a function that did anything except return `undefined`.
            // 3. In the PR where this comment was written, I discovered that changing the `lines` prop would not update the chart. Suspecting this was because of the bizarre `useEffect`/`useRef` combination, I removed `onChartClick` (unused), `useRef`, and `useEffect` from this component.
            // 4. Changing `lines` still wasn't causing the chart to re-render. I turned to Claude Code for help, and it suggested the use of this `notMerge` prop. I investigated online and discovered similar suggestions for similar situations on StackOverflow. I looked at the ECharts docs on "Component Merging Modes" (https://echarts.apache.org/en/api.html#echartsInstance.setOption). That didn't help me understand much, because those docs are not great.
            //    - Fortunately, all the complexity in understanding how ECharts behaves seems to occur when `notMerge` is false (the default), whereas using `notMerge` seems straightforward: "If opts.notMerge is set as true, the old components will be totally removed and the new component will be created by the incoming option." I can't see a reason not to use `notMerge` as long as we aren't seeing performance issues (highly unlikely in charts as simple as ours).
            // 5. Since adding `notMerge` does seem to solve all of the problems we've run into in the past--there is no flickering of the chart (probably unrelated to this component anyway), and changing the props actually causes the chart to re-render with new data--this new use of `notMerge` was committed.
            //    - Note that before this PR, the chart *would* re-render if *empty* arrays were passed in for one or more `lines` (possibly due to specifics of the merging strategies that are utilized when `notMerge` is false?). But this was the one exception to the rule all of our chart components have that arrays of data and x-axes passed into components all have the same length. (I think this was an implicit rule, anyway. Before this PR, no chart-related props, interfaces, or components were documented; several props were unused/untested; and some bits of logic seemed to accomplish nothing. This PR documents current state to the best of my ability.)
            //    - So, until/unless this rule is changed, I'm forcing this component to align with all other charts components. In an ideal world with unlimited time, Dashboards would be completely rewritten with interfaces and props that are easy to understand. Barring that possibility, these are the things developers should consider if/when more refactoring/standardization is applied to charts components:
            //        - Is there a more correct way to solve the flickering problem? If callbacks were truly the culprit, maybe parents should just memoize the callbacks?
            //        - Should `notMerge` be used?
            //        - Lots of code is copied/pasted between charts components. Should some components be combined? Should a shared, internal component be wrapped by similar chart components? It's a major pain to update a dozen files whenever a copied+pasted bug is found.
            // 6. I typed all of the above and then asked Claude to review the comments and provide answers if available. It had this to say (before trusting this info, keep in mind the caveat that neither I nor Claude know what "flickering" actually meant):
            //    - Flickering typically comes from the chart being destroyed and recreated, not from callback references changing. The useRef/useEffect pattern wouldn't have directly solved flickering - if it appeared to fix it, the actual fix was likely something else in that PR (or coincidental).
            //    - Ironically, notMerge={false} (the default) is usually better for avoiding flicker because it smoothly transitions data rather than replacing the whole chart. If flickering returns, the solutions would be: React.memo on the component, memoizing the options object, or investigating what's causing full unmount/remount cycles.
            //    - ECharts' default merge behavior matches series by index or name. If the matching logic doesn't align with how your data changes, updates can silently fail. This is the main footgun with notMerge={false} - you need to understand ECharts' merge semantics to use it correctly. notMerge={true} bypasses this entirely.
            notMerge
        />
    );
};
